import { isInline } from './element';
import { hasAncestor, isElement } from './node';

export const METADATA_TAG_NAMES = new Set([
  'BASE',
  'COMMAND',
  'LINK',
  'META',
  'NOSCRIPT',
  'SCRIPT',
  'STYLE',
  'TITLE',
]);

export function isMetadataContent(node: Node | null): boolean {
  return Boolean(node && isElement(node) && METADATA_TAG_NAMES.has(node.tagName));
}

// Not an official category, but elements that are meant for layout purposes and should not directly contain text nodes.
export const LAYOUT_TAG_NAME = new Set([
  'TABLE',
  'TBODY',
  'TR',
  'THEAD',
  'TFOOT',
  'COLGROUP',
  'COL',
  'OL',
  'UL',
  'DL',
  'MENU',
  'PICTURE',
  'SELECT',
  'OPTGROUP',
]);

export function isLayoutElement(node: Node | null): boolean {
  return Boolean(node && isElement(node) && LAYOUT_TAG_NAME.has(node.tagName));
}

// Not an official HTML 5 content category, but this is roughly the flow content that is not phrasing content.
// https://developer.mozilla.org/en-US/docs/Web/HTML/Block-level_elements
export const BLOCK_LEVEL_TAG_NAMES = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DETAILS',
  'DIALOG',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HGROUP',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'UL',
]);

export function isBlockLevelContent(node: Node | null): boolean {
  return !!node && isElement(node) && BLOCK_LEVEL_TAG_NAMES.has(node.tagName) && !isInline(node);
}

export const PHRASING_CONTENT_TAG_NAMES = new Set([
  'ABBR',
  'AUDIO',
  'B',
  'BDI',
  'BDO',
  'BR',
  'BUTTON',
  'CANVAS',
  'CITE',
  'CODE',
  'COMMAND',
  'DATA',
  'DATALIST',
  'DFN',
  'EM',
  'EMBED',
  'FONT',
  'I',
  'IFRAME',
  'IMG',
  'INPUT',
  'KBD',
  'KEYGEN',
  'LABEL',
  'MARK',
  'MATH',
  'METER',
  'NOSCRIPT',
  'OBJECT',
  'OUTPUT',
  'PROGRESS',
  'Q',
  'RUBY',
  'S',
  'SAMP',
  'SCRIPT',
  'SELECT',
  'SMALL',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'SVG',
  'TEXTAREA',
  'TIME',
  'U',
  'VAR',
  'VIDEO',
  'WBR',
]);

const PHRASING_CONTENT_CONTAINING_TAG_NAMES = new Set(['A', 'DEL', 'INS', 'LINK', 'MAP']);

export function isPhrasingContent(node: Node | null): boolean {
  if (!node) {
    return false;
  }

  switch (node.nodeType) {
    case Node.ELEMENT_NODE: {
      const element = node as Element;

      if (element.tagName === 'AREA') {
        const document = element.ownerDocument;
        return (
          !!document &&
          hasAncestor(element, ancestor => isElement(ancestor) && ancestor.tagName === 'MAP', document.body)
        );
      }

      if (PHRASING_CONTENT_TAG_NAMES.has(element.tagName)) {
        return true;
      }

      if (PHRASING_CONTENT_CONTAINING_TAG_NAMES.has(element.tagName)) {
        const { childNodes } = element;
        const { length } = childNodes;
        let isEveryChildPhrasingContent = true;

        // Optimized by using a simple loop, because isPhrasingContent is called a lot.
        for (let i = 0; isEveryChildPhrasingContent && i < length; i++) {
          isEveryChildPhrasingContent = isEveryChildPhrasingContent && isPhrasingContent(childNodes[i]);
        }

        return isEveryChildPhrasingContent;
      }

      return false;
    }
    case Node.TEXT_NODE:
      // Because text in a <style>, etc tag is NOT phrasing content.
      return !node.parentNode || !(isMetadataContent(node.parentNode) || isLayoutElement(node.parentNode));

    default:
      return false;
  }
}

export const PRESENTATIONAL_PHRASING_CONTENT_TAG_NAMES = new Set([
  'B',
  'CODE',
  'EM',
  'FONT',
  'I',
  'MARK',
  'S',
  'SMALL',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'U',
]);

export function isPresentationalPhrasingContent(node: Node | null): boolean {
  if (!node) {
    return false;
  }

  switch (node.nodeType) {
    case Node.ELEMENT_NODE: {
      const element = node as Element;

      return PRESENTATIONAL_PHRASING_CONTENT_TAG_NAMES.has(element.tagName);
    }
    case Node.TEXT_NODE:
      return true;

    default:
      return false;
  }
}

export const EMBEDDED_CONTENT_TAG_NAMES = new Set([
  'AUDIO',
  'CANVAS',
  'EMBED',
  'IFRAME',
  'IMG',
  'MATH',
  'OBJECT',
  'SVG',
  'VIDEO',
]);

export function isEmbeddedContent(node: Node | null): boolean {
  return isElement(node) && EMBEDDED_CONTENT_TAG_NAMES.has(node.tagName);
}

export const EDITOR_DATA_LIST_TYPE = 'data-editor-list-type';

export const EDITOR_SELECTED_ELEMENT = 'data-editor-selected';

export const EDITOR_DATA_ATTRIBUTE_NAMES = new Set([EDITOR_DATA_LIST_TYPE, EDITOR_SELECTED_ELEMENT]);

/**
 * Removes attr added for internal purposes
 * @param fragment
 */
export function removeEditorDataAttributes(fragment: DocumentFragment) {
  EDITOR_DATA_ATTRIBUTE_NAMES.forEach(attr => {
    fragment.querySelectorAll(`[${attr}]`).forEach(element => element.removeAttribute(attr));
  });
}
